/*
 * /telemedicine/messages — staff message center.
 * Shows all patient messaging sessions grouped by time period.
 * Staff can respond to any session and close open ones.
 */
import { Container, Page, PageHeader, Placeholder } from "@emr/react-ui"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Clock, ExternalLink, MessageSquare, User, X } from "lucide-react"
import { useState } from "react"
import { useAuth } from "@emr/auth/client"
import { TelemedicineChatPanel } from "../../components/telemedicine-chat-panel"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/telemedicine/messages")({
	component: TelemedicineMessagesPage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

/* ─── Types ─────────────────────────────────────────────────────────────────  */

type Session = {
	id: string
	patientId: string
	active: boolean
	createdAt: Date | string
	patientFirstName: string | null
	patientLastName: string | null
	isUnanswered: boolean
	lastMessage: {
		message: string
		senderName: string
		senderRole: string
		createdAt: Date | string
	} | null
}

type TimeGroup = "today" | "thisWeek" | "lastThirtyDays" | "older"

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function getTimeGroup(d: Date | string): TimeGroup {
	const date   = new Date(d)
	const now    = new Date()
	const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const monday = new Date(today)
	monday.setDate(today.getDate() - ((today.getDay() + 6) % 7)) // start of week (Mon)
	const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000)

	if (date >= today)           return "today"
	if (date >= monday)          return "thisWeek"
	if (date >= thirtyDaysAgo)   return "lastThirtyDays"
	return "older"
}

const GROUP_LABELS: Record<TimeGroup, string> = {
	today:          "Today",
	thisWeek:       "This Week",
	lastThirtyDays: "Last 30 Days",
	older:          "Older",
}
const GROUP_ORDER: TimeGroup[] = ["today", "thisWeek", "lastThirtyDays", "older"]

function timeAgo(d: Date | string): string {
	const diff = Date.now() - new Date(d).getTime()
	const mins = Math.floor(diff / 60000)
	if (mins < 1)  return "just now"
	if (mins < 60) return `${mins}m ago`
	const hrs = Math.floor(mins / 60)
	if (hrs < 24)  return `${hrs}h ago`
	return new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function TelemedicineMessagesPage() {
	const { user } = useAuth()
	const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)

	const { data: sessions = [], isLoading, refetch } =
		trpc.telemedicineChat.getAllMessagingSessions.useQuery(undefined, {
			refetchInterval: 15000,
		})

	const closeConversation = trpc.telemedicine.closeConversation.useMutation({
		onSuccess: () => refetch(),
	})

	// Group sessions by time period (use last-message time if available, else session creation)
	const grouped = (() => {
		const groups: Partial<Record<TimeGroup, Session[]>> = {}
		for (const s of sessions as Session[]) {
			const anchor = s.lastMessage?.createdAt ?? s.createdAt
			const key = getTimeGroup(anchor)
			if (!groups[key]) groups[key] = []
			groups[key]!.push(s)
		}
		return groups
	})()

	const selectedSession = (sessions as Session[]).find((s) => s.id === selectedSessionId)

	return (
		<Page>
			<Container>
				<PageHeader title="Patient Messages" />
				<p className="text-sm text-muted-foreground mb-6">
					All patient messaging conversations. Respond to any session — open or closed.
				</p>

				{isLoading ? (
					<Placeholder title="Loading…" />
				) : sessions.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-20 gap-3">
						<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
							<MessageSquare className="w-8 h-8 text-muted-foreground" />
						</div>
						<p className="text-sm font-medium text-foreground">No conversations yet</p>
						<p className="text-xs text-muted-foreground">Patient messages will appear here.</p>
					</div>
				) : (
					<div className="flex gap-4 h-[calc(100vh-240px)] min-h-[400px]">
						{/* Session list */}
						<div className="w-80 shrink-0 flex flex-col gap-5 overflow-y-auto pr-1">
							{GROUP_ORDER.map((group) => {
								const list = grouped[group]
								if (!list?.length) return null
								return (
									<div key={group}>
										<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
											{GROUP_LABELS[group]}
										</p>
										<div className="flex flex-col gap-1.5">
											{list.map((s) => {
												const name = [s.patientFirstName, s.patientLastName].filter(Boolean).join(" ") || "Patient"
												const isSelected = s.id === selectedSessionId
												return (
													<button
														key={s.id}
														onClick={() => setSelectedSessionId(s.id)}
														className={`w-full text-left rounded-xl border px-3 py-3 transition-colors ${
															isSelected
																? "bg-primary/10 border-primary/40"
																: "bg-card border-border hover:bg-muted"
														}`}
													>
														<div className="flex items-start gap-2">
															<div className="mt-0.5 w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
																<User className="w-3.5 h-3.5 text-muted-foreground" />
															</div>
															<div className="flex-1 min-w-0">
																<div className="flex items-center justify-between gap-1">
																	<p className="text-sm font-medium truncate">{name}</p>
																	<div className="flex items-center gap-1 shrink-0">
																		{!s.active && (
																			<span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
																				Closed
																			</span>
																		)}
																		{s.isUnanswered && (
																			<span className="w-2 h-2 rounded-full bg-amber-500" title="Unanswered" />
																		)}
																	</div>
																</div>
																{s.lastMessage && (
																	<p className="text-xs text-muted-foreground truncate mt-0.5">
																		{s.lastMessage.senderRole !== "patient" && (
																			<span className="text-primary/70">You: </span>
																		)}
																		{s.lastMessage.message}
																	</p>
																)}
																{s.lastMessage?.createdAt && (
																	<div className="flex items-center gap-1 mt-1">
																		<Clock className="w-3 h-3 text-muted-foreground" />
																		<span className="text-xs text-muted-foreground">
																			{timeAgo(s.lastMessage.createdAt)}
																		</span>
																	</div>
																)}
															</div>
														</div>
													</button>
												)
											})}
										</div>
									</div>
								)
							})}
						</div>

						{/* Chat panel */}
						<div className="flex-1 rounded-xl border border-border overflow-hidden bg-gray-900 flex flex-col min-h-0">
							{selectedSession ? (
								<>
									{/* Chat panel header with patient link + close button */}
									<div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-700 bg-gray-800 shrink-0">
										<div className="flex items-center gap-2">
											<User className="w-4 h-4 text-gray-400" />
											<span className="text-sm font-medium text-white">
												{[selectedSession.patientFirstName, selectedSession.patientLastName].filter(Boolean).join(" ") || "Patient"}
											</span>
											{!selectedSession.active && (
												<span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">Closed</span>
											)}
										</div>
										<div className="flex items-center gap-2">
											<Link
												to="/patient/$patientId"
												params={{ patientId: selectedSession.patientId }}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-gray-700"
											>
												<ExternalLink className="w-3.5 h-3.5" />
												Open Patient
											</Link>
											{selectedSession.active && (
												<button
													onClick={() => closeConversation.mutate({ sessionId: selectedSession.id })}
													disabled={closeConversation.isPending}
													className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 transition-colors px-2 py-1 rounded hover:bg-gray-700"
													title="Close conversation"
												>
													<X className="w-3.5 h-3.5" />
													Close
												</button>
											)}
										</div>
									</div>
									<TelemedicineChatPanel
										sessionId={selectedSession.id}
										myUserId={user?.id}
										className="flex-1 min-h-0"
									/>
								</>
							) : (
								<div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
									<MessageSquare className="w-10 h-10 text-gray-600" />
									<p className="text-sm text-gray-400">Select a conversation on the left to view and respond.</p>
								</div>
							)}
						</div>
					</div>
				)}
			</Container>
		</Page>
	)
}
